import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import NoticeBar from 'components/NoticeBar'
import { button } from '@groovehq/internal-design-system/lib/styles/elements'
import { DRAWER_TYPE_BILLING_DOWNGRADE_PLAN_CONFIRMATION } from 'ducks/drawers/types'
import { useDrawer } from 'ducks/drawers/hooks'
import {
  selectFuturePhase,
  selectPendingFuturePhaseDetailed,
} from 'ducks/billing/selectors'
import { diff } from 'util/date'
import { PLAN_DOWNGRADABLE_DIFF } from 'constants/billing'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import { styles } from './styles'

const BillingPlanLimited = () => {
  const { startDate } = useSelector(selectFuturePhase)
  const overdue =
    diff('milliseconds', new Date(), startDate * 1000) < PLAN_DOWNGRADABLE_DIFF

  const {
    drawerId: billingDowngradeConfirmationDrawerId,
    openDrawer: openBillingDowngradeConfirmation,
  } = useDrawer({
    type: DRAWER_TYPE_BILLING_DOWNGRADE_PLAN_CONFIRMATION,
  })

  const pendingFuturePhaseByProduct = useSelector(
    selectPendingFuturePhaseDetailed
  )

  // product that is getting downgraded will be on top
  const pendingFuturePhases = useMemo(
    () =>
      Object.values(pendingFuturePhaseByProduct).sort(
        (a, b) => (b?.isPlanChange || false) - (a?.isPlanChange || false)
      ),
    [pendingFuturePhaseByProduct]
  )

  const handleClick = useCallback(
    () => {
      if (pendingFuturePhases.length > 0) {
        const { planId, phaseProduct } = pendingFuturePhases[0]
        let otherProductQueryParams = {}

        // Always send the other product as well
        // even though it is not being changed, there is a chance that it is over feature limit
        if (pendingFuturePhases.length > 1) {
          const {
            planId: otherProductPlanId,
            phaseProduct: otherProductPhaseProduct,
          } = pendingFuturePhases[1]

          otherProductQueryParams = {
            ...buildDrawerQueryParam(
              billingDowngradeConfirmationDrawerId,
              'drawerOtherProductPlanId',
              otherProductPlanId
            ),
            ...buildDrawerQueryParam(
              billingDowngradeConfirmationDrawerId,
              'drawerOtherProductProductType',
              otherProductPhaseProduct
            ),
          }
        }

        openBillingDowngradeConfirmation(planId, {
          query: {
            ...buildDrawerQueryParam(
              billingDowngradeConfirmationDrawerId,
              'drawerProductType',
              phaseProduct
            ),
            ...otherProductQueryParams,
          },
        })
      }
    },
    [
      openBillingDowngradeConfirmation,
      billingDowngradeConfirmationDrawerId,
      pendingFuturePhases,
    ]
  )

  return (
    <NoticeBar
      forceShow={false}
      dismissible={!overdue}
      flagName={
        overdue ? undefined : `has_seen_billing_plan_usage_limited_${startDate}`
      }
      css={[
        styles.billingPlanLimitedAlert,
        overdue && styles.billingPlanLimitedOverdueAlert,
      ]}
      className="grui py-5"
    >
      {overdue
        ? `Your account's feature usage exceeds your new selected plan, this will
      lead to an automatic upgrade if not adjusted.`
        : `Your account is currently using features in excess of its scheduled downgrade.`}
      <button
        className="grui mx-5"
        css={button.styles.secondaryLink}
        onClick={handleClick}
      >
        Learn more
      </button>
    </NoticeBar>
  )
}

export default BillingPlanLimited
